/**
 * Where the MCP gateway lives — or that it lives nowhere.
 *
 * Three call sites (kg-provider, ontology-inspector, mutation-client-init)
 * each wrote the same two lines:
 *
 *   const MCP_GATEWAY_URL =
 *     (import.meta.env.VITE_KG_MCP_GATEWAY as string | undefined) ??
 *     'https://<project>.supabase.co/functions/v1/cogo-mcp-gateway'
 *
 * `??` falls back only on null/undefined, so there was no way to say "this
 * deployment has no gateway" — every value except a real URL still produced
 * the cloud literal. On the DGX appliance, which has no public internet and no
 * local implementation of that gateway protocol, that literal is what shipped:
 * measured in the appliance artifact for Studio 855ee1b4, the public project
 * host appeared 13 times, and after the build began overriding the Supabase
 * variables it still appeared 3 times — those three being exactly these
 * literals. A build-time override cannot reach a string constant.
 *
 * The gateway is a FALLBACK, not a dependency: `HybridRepository` declares
 * `componentFallback?` optional and exposes `hasComponentFallback()`, so
 * "absent" is a state the repository already understands. This module lets a
 * deployment express it: set `VITE_KG_MCP_GATEWAY` to an empty value and the
 * gateway is off, rather than pointed at a host the network cannot reach.
 */

/** The cloud gateway used when a deployment does not say otherwise. */
export const DEFAULT_MCP_GATEWAY_URL =
  'https://cjvgmyotqxfpxpvmwxfv.supabase.co/functions/v1/cogo-mcp-gateway'

/**
 * The gateway URL for this build, or `null` when the deployment declared it
 * has none.
 *
 * Unset (the variable is absent) keeps the cloud default — the cloud build's
 * behaviour is unchanged. Set-but-empty, or whitespace, means "no gateway":
 * an empty string is a deliberate statement, not a missing value, and it is
 * the only thing a shell-provided `VITE_KG_MCP_GATEWAY=` can express.
 */
export function resolveMcpGatewayUrl(): string | null {
  const configured = (
    import.meta.env as Record<string, string | undefined>
  ).VITE_KG_MCP_GATEWAY

  if (configured === undefined) return DEFAULT_MCP_GATEWAY_URL
  const trimmed = configured.trim()
  return trimmed === '' ? null : trimmed
}
